// One-shot reader for the legacy device storage. During the WASM
// migration we keep the legacy `devices` / `keyrings` stores intact and
// just read the master seed out so it can be re-wrapped into the new
// passphrase-protected envelope format.
//
// The legacy format stores:
//   - DeviceMaterial.wrappedMasterSeed: AES-GCM ciphertext (base64)
//   - DeviceMaterial.wrapIV: AES-GCM 12-byte IV (base64)
//   - wrapKey: non-extractable AES CryptoKey kept in IndexedDB
//
// Unwrap is: crypto.subtle.decrypt({AES-GCM, iv}, wrapKey, ct) → 32 bytes.
// This is the same three lines the legacy `loadDevice()` does at the
// top of devicePersistence.secure.ts.

import {
    retrieveDeviceMaterial,
    retrieveWrapKey,
    getCurrentDeviceId,
} from '../crypto/indexeddb.secure'
import { base64ToBytes } from '../crypto/utils'

export interface LegacyDeviceImport {
    seedBytes: Uint8Array // 32 bytes; caller must .fill(0) after use
    seedKid: string
    sigKid: string
    encKid: string
    aesKid: string
    nickname?: string
}

// Returns the raw seed + kids from the legacy storage. The caller
// owns `seedBytes` and is expected to zero it as soon as it's been
// consumed (e.g., passed to practicemdWASM.encryptSeed).
export async function importLegacyDevice(): Promise<LegacyDeviceImport|null> {
    const seedKid = await getCurrentDeviceId()
    if (!seedKid) {
        return null
    }
    const material = await retrieveDeviceMaterial(seedKid)
    if (!material) {
        throw new Error(`legacy DeviceMaterial missing for seedKid ${seedKid}`)
    }
    const wrapKey = await retrieveWrapKey(seedKid)
    if (!wrapKey) {
        throw new Error(`legacy wrap key missing for seedKid ${seedKid}`)
    }

    const ct = base64ToBytes(material.wrappedMasterSeed)
    const iv = base64ToBytes(material.wrapIV)
    const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        wrapKey,
        ct
    )
    const seedBytes = new Uint8Array(decrypted)
    if (seedBytes.length !== 32) {
        throw new Error(
            `legacy seed has wrong length ${seedBytes.length} (expected 32)`
        )
    }

    return {
        seedBytes,
        seedKid: material.seedKid,
        sigKid: material.sigKid,
        encKid: material.encKid,
        aesKid: material.aesKid,
        nickname: material.nickname,
    }
}

// Helper for converting raw seed bytes to the hex string the WASM
// bindings accept. Keep this close to importLegacyDevice so callers
// don't reach for a generic hex library.
export function bytesToHex(bytes: Uint8Array): string {
    let out = ''
    for (let i = 0; i < bytes.length; i++) {
        out += bytes[i].toString(16).padStart(2, '0')
    }
    return out
}
